import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 39000-39999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const assetsErrors = {
  // 39xxx is the assets module's series. assets is unusual: it already routes every failure through
  // three `failed*Write(message, code)` helpers, and most call sites already pass a SPECIFIC code —
  // so only the sites relying on each helper's `= errorCodes.SOMETHING_WENT_WRONG` default were in
  // scope. Those helpers require `message`, so these sites KEEP their sentence and gain a code.
  // Two sites reused PROJECT_NOT_FOUND and SPACE_NOT_FOUND (7015).
  // 39001-39010 — the assets module's split.
  ASSET_BULK_UPDATE_FAILED: {
    errorCode: 39001,
    errorMessage: t("Failed to Updated Asset(s)"),
    isDynamic: false,
  },
  ASSET_CREATE_FAILED: {
    errorCode: 39002,
    errorMessage: t("Failed to created Asset"),
    isDynamic: false,
  },
  ASSET_UPDATE_FAILED: {
    errorCode: 39003,
    errorMessage: t("Failed to update Asset Details!"),
    isDynamic: false,
  },
  ASSET_TEMPLATE_SET_FAILED: {
    errorCode: 39004,
    errorMessage: t("Failed to set Template"),
    isDynamic: false,
  },
  ASSET_TEMPLATE_TO_ASSIGN_NOT_FOUND: {
    errorCode: 39005,
    errorMessage: t("The Template to assign no longer exists."),
    isDynamic: false,
  },
  // `failedAssetTypeWrite`'s DEFAULT arguments: its default message is "Failed to save Asset Type",
  // so the helper's default code becomes this instead of the generic — the pair now matches.
  ASSET_TYPE_SAVE_FAILED: {
    errorCode: 39006,
    errorMessage: t("Failed to save Asset Type"),
    isDynamic: false,
  },
  ASSET_TYPE_MANDATORY_FIELD_EMPTY: {
    errorCode: 39007,
    errorMessage: t("Mandatory field is Empty"),
    isDynamic: false,
  },
  ASSET_TYPE_NOT_FOUND: {
    errorCode: 39008,
    errorMessage: t("Asset Type not found"),
    isDynamic: false,
  },
  ASSET_IMPORT_FAILED: {
    errorCode: 39009,
    errorMessage: t("Failed to import assets"),
    isDynamic: false,
  },
  ASSET_NOT_FOUND: {
    errorCode: 39010,
    errorMessage: t("Asset not found"),
    isDynamic: false,
  },
  ASSET_TAG_ALREADY_EXISTS: {
    errorCode: 39011,
    errorMessage: t("This Tag already exists. Please enter a unique Tag."),
    isDynamic: false,
  },
  ASSET_IMPORT_NO_ROWS: {
    errorCode: 39012,
    errorMessage: t("Failed to Import Assets — no rows were sent."),
    isDynamic: false,
  },
  // 39014-39030: the assets module's VALIDATION_FAILED / NO_CHANGES_APPLIED / ASSET_TYPE_SAVE_FAILED
  // split. Those three codes were carrying 17 different sentences between them, so a refusal could
  // not be told apart from its code alone — each distinct refusal now has its own.
  ASSET_DATA_REQUIRED: {
    errorCode: 39014,
    errorMessage: t("Asset data is required"),
    isDynamic: false,
  },
  ASSET_MANDATORY_FIELDS_MISSING: {
    errorCode: 39015,
    errorMessage: t("Mandatory fields not filled"),
    isDynamic: false,
  },
  ASSET_PROJECT_ID_REQUIRED: {
    errorCode: 39016,
    errorMessage: t("Project ID is required"),
    isDynamic: false,
  },
  ASSET_CUSTOM_FIELDS_INVALID: {
    errorCode: 39017,
    errorMessage: t("Custom Fields validation failed"),
    isDynamic: false,
  },
  ASSET_BULK_UPDATE_NONE_SELECTED: {
    errorCode: 39018,
    errorMessage: t("No assets were selected for the bulk update."),
    isDynamic: false,
  },
  ASSET_BULK_UPDATE_FIELDS_MISSING: {
    errorCode: 39019,
    errorMessage: t("Missing required fields for bulk update"),
    isDynamic: false,
  },
  ASSET_ID_REQUIRED_FOR_UPDATE: {
    errorCode: 39020,
    errorMessage: t("Asset ID is required for update"),
    isDynamic: false,
  },
  ASSET_TYPE_ID_NOT_FOUND: {
    errorCode: 39021,
    errorMessage: t("Asset id not found"),
    isDynamic: false,
  },
  ASSET_TYPE_NAME_DUPLICATE: {
    errorCode: 39022,
    errorMessage: t("This Type already exists. Please enter a unique Name."),
    isDynamic: false,
  },
  ASSET_TYPE_IN_USE_CANNOT_EDIT: {
    errorCode: 39023,
    errorMessage: t("This Asset Type cannot be Edited because it is currently in use."),
    isDynamic: false,
  },
  ASSET_TYPE_UPDATE_FAILED: {
    errorCode: 39024,
    errorMessage: t("Failed to update Asset Type"),
    isDynamic: false,
  },
  ASSET_TYPE_NONE_SELECTED: {
    errorCode: 39025,
    errorMessage: t("No Assets Selected"),
    isDynamic: false,
  },
  ASSET_TYPE_LAST_CANNOT_DELETE: {
    errorCode: 39026,
    errorMessage: t("Last Asset Type cannot be deleted"),
    isDynamic: false,
  },
  ASSET_TYPE_IN_USE_CANNOT_DELETE: {
    errorCode: 39027,
    errorMessage: t("This Asset Type cannot be deleted because it is currently in use."),
    isDynamic: false,
  },
  ASSET_TYPE_DELETE_FAILED: {
    errorCode: 39028,
    errorMessage: t("Failed to deleted Asset Type(s)"),
    isDynamic: false,
  },
  ASSET_DELETE_NONE_SELECTED: {
    errorCode: 39029,
    errorMessage: t("No documents Selected"),
    isDynamic: false,
  },
  ASSET_DELETE_FAILED: {
    errorCode: 39030,
    errorMessage: t("Failed to deleted Asset(s)"),
    isDynamic: false,
  },
  ASSET_NONE_VALID: {
    errorCode: 39013,
    errorMessage: t("No valid Assets found"),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
